import { DOCUMENT } from '@angular/common';
import { WINDOW } from '@shared/tokens';
import {
	AfterViewInit,
	Component,
	ComponentFactoryResolver,
	EventEmitter,
	HostListener,
	Inject,
	Injector,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	SimpleChanges,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation,
	EnvironmentInjector,
	NgZone
} from '@angular/core';
import { AppFacade, RxSavedPayload } from './app.facade';
import { from, fromEvent, merge, Observable, of, Subject, combineLatest, debounceTime, startWith } from 'rxjs';
import { catchError, delay, distinctUntilChanged, exhaustMap, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { SaveRxService } from '@modules/rx-for-doctor/services/save-rx.service';
import { TranslateService, TranslationChangeEvent } from '@ngx-translate/core';
import { resetStores } from '@datorama/akita';
import { LoggerService } from '@core/services/logger/logger.service';
import { RxComponents } from '@shared/models/rx-models/interfaces/rx-components-model';
import { RoleTypeEnum } from '@shared/models/role-type';
import { debounceDelay, emptyGuid, ViewModeEnum } from '@shared/models/consts';
import { MatIconService } from '@shared/services/mat-icons.service';
import { localSettingsStoreName } from '@shared/store/localSettings/local-settings-store';
import { PrintOrientation } from '@shared/models/enums/enums';
import { ShellStore } from '@shared/store/shell/shell-store';
import { RxNote } from '@modules/notes/models/rx-note';
import { NotesService } from '@modules/notes/services/notes.service';
import { PRINT_IFRAME_ELEMENT, PrintRxComponent } from '@modules/print-rx/containers/print-rx/print-rx.component';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderInformationModel } from '@shared/models/rx-models/interfaces/order-information-model';
import { NotesQuery } from '@modules/notes/state/notes-query';
import { RxRulesService } from '@shared/services/rx-rules-helper/rx-rules.service';
import { Version as rxAppVersion } from '../environments/app-version';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { ChangeUnitTypeInput } from '@shared/models/change-unit-type-input';
import { PrintService } from '@shared/services/print.service';
import { AligntechNotesService } from '@modules/aligntech-notes/services/aligntech-notes.service';
import { TeethNumberingSystem } from '@modules/teeth-diagram/models/teeth-numbering-system.enum';
import { TeethUnitType } from '@shared/models/rx-rules-json-interface';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { AuthInfo } from '@shared/models/auth-info';
import { TracesQuery } from '@shared/store/traces/traces-query';
import { ReadOnlyRulesService } from '@shared/services/readOnlyRules.service';
import { FontFaceService } from '@core/services/font-face.service';
import { v4 as uuid } from 'uuid';
import { RxModelApi } from '@shared/models/rx-models/interfaces/rx-model-api';
import { type RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { SplunkRum } from '@splunk/otel-web';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { initializeRemoteApplicationLoader } from '@itero/ui-toolkit-angular/remote-application';
import { CustomMaterialsService } from '@shared/services/custom-materials.service';
import { RxSummaryService } from '@shared/services/rx-summary.service';
import { UnifiedPatientsIntegrationService } from '@shared/services/unified-patients-integration.service';

export interface AppComponentInputs {
	orderId: number;
	rxId: string;
	companyId: number;
	doctorId: number;
	contactId: number;
	languageCode: string;
	apiEndpoint: string;
	scannerId: string;
	runMode: string;
	clientVersion: string;
	packageVersion: string;
	isReadOnly: boolean;
	readOnlyRules: RxComponents;
	applicationMode: string;
	isOnline: boolean | null;
	productType: string;
	isRxTakenForScan: boolean;
	authToken: string;
	validationMode: string;
	patientGuid: string;
	clonedFromRxId: string;
	userRole: RoleTypeEnum;
	rxIdsForPrint: number[];
	triggerPrintRx: boolean;
	orientation: string;
	forceV0: boolean;
	staticFilesEndpoint: string;
	isReturn: boolean;
	isResendScan: boolean;
	shouldAnonymizeRx: boolean;
	autoDisplayPrintViewInTheDOM: boolean;
	isHeadlessPrint: boolean;
	enableAllCaseTypesForAddRx: boolean;
	shouldCheckSleeveConfirmationCheckBox: boolean;
	forceDisplayTeethNumberingSystem: TeethNumberingSystem;
	printStylesName: string;
	authInfo: AuthInfo;
	rxAppApiEndpoint: string;
	patientAppApiEndpoint: string;
	saveRx: string;
	saveRxScanner: string;
	validateBeforeSend: string;
	addNote: RxNote;
	changeUnitType: ChangeUnitTypeInput;
	orderInformation: OrderInformationModel;
	prepareRx: number;
	getUnitType: number;
	region?: string;
	hasOuterDestroyable?: boolean;
	viewMode?: ViewModeEnum;
}

@Component({
	selector: 'rx-app',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [AppFacade, PrintService],
	host: { class: 'mat-typography' }
})
export class AppComponent extends BaseDestroyableComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
	@ViewChild('printRxOutlet', { read: ViewContainerRef }) private printVcr: ViewContainerRef;
	private readonly legacyToolkitFontsBodyClass = 'rx-app-legacy-toolkit-fonts';

	constructor(
		private facade: AppFacade,
		private translateService: TranslateService,
		private saveRxService: SaveRxService,
		private logger: LoggerService,
		private matIconService: MatIconService,
		private shellStore: ShellStore,
		public notesService: NotesService,
		private vcRef: ViewContainerRef,
		private cfr: ComponentFactoryResolver,
		private shellQuery: ShellQuery,
		private notesQuery: NotesQuery,
		private rxRulesService: RxRulesService,
		private patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private printService: PrintService,
		private injector: Injector,
		private renderer: Renderer2,
		public aligntechNotesService: AligntechNotesService,
		private hostPlatformService: HostPlatformService,
		private tracesQuery: TracesQuery,
		private readOnlyRulesService: ReadOnlyRulesService,
		private fontFaceService: FontFaceService,
		private readonly appSettingsService: AppSettingsService,
		private readonly ngZone: NgZone,
		private readonly environmentInjector: EnvironmentInjector,
		private readonly customMaterialsService: CustomMaterialsService,
		private readonly rxSummaryService: RxSummaryService,
		private readonly unifiedPatientsIntegrationService: UnifiedPatientsIntegrationService,
		@Inject(DOCUMENT) private readonly documentRef: Document,
		@Inject(WINDOW) private readonly windowRef: Window
	) {
		super();
	}

	private readonly componentName = 'AppComponent';

	@Input() contactId: AppComponentInputs['contactId'];
	@Input() doctorId: AppComponentInputs['doctorId'];
	@Input() companyId: AppComponentInputs['companyId'];
	@Input() languageCode: AppComponentInputs['languageCode'];
	@Input() staticFilesEndpoint: AppComponentInputs['staticFilesEndpoint'];
	@Input() apiEndpoint: AppComponentInputs['apiEndpoint'];
	@Input() rxAppApiEndpoint: AppComponentInputs['rxAppApiEndpoint'];
	@Input() patientAppApiEndpoint: AppComponentInputs['patientAppApiEndpoint'];
	@Input() scannerId: AppComponentInputs['scannerId'];
	@Input() runMode: AppComponentInputs['runMode'];
	@Input() clientVersion: AppComponentInputs['clientVersion'];
	@Input() packageVersion: AppComponentInputs['packageVersion'];
	@Input() applicationMode: AppComponentInputs['applicationMode'];
	@Input() isOnline: AppComponentInputs['isOnline'];
	@Input() productType: AppComponentInputs['productType'];
	@Input() rxId: AppComponentInputs['rxId'];
	@Input() isRxTakenForScan: AppComponentInputs['isRxTakenForScan'];
	@Input() orderId: AppComponentInputs['orderId'];
	@Input() saveRx: AppComponentInputs['saveRx'];
	@Input() saveRxScanner: AppComponentInputs['saveRxScanner'];
	@Input() validateBeforeSend: AppComponentInputs['validateBeforeSend'];
	@Input() isReadOnly: AppComponentInputs['isReadOnly'];
	@Input() authToken: AppComponentInputs['authToken'];
	@Input() readOnlyRules: AppComponentInputs['readOnlyRules'];
	@Input() patientGuid: AppComponentInputs['patientGuid'];
	@Input() validationMode: AppComponentInputs['validationMode'];
	@Input() userRole: AppComponentInputs['userRole'];
	@Input() clonedFromRxId: AppComponentInputs['clonedFromRxId'];
	@Input() rxIdsForPrint: AppComponentInputs['rxIdsForPrint'];
	@Input() triggerPrintRx: AppComponentInputs['triggerPrintRx'];
	@Input() isHeadlessPrint: AppComponentInputs['isHeadlessPrint'];
	@Input() orientation = 'portrait';
	@Input() forceV0: AppComponentInputs['forceV0'];
	@Input() isReturn: AppComponentInputs['isReturn'];
	@Input() isResendScan: AppComponentInputs['isResendScan'];
	@Input() addNote: AppComponentInputs['addNote'];
	@Input() changeUnitType: AppComponentInputs['changeUnitType'];
	@Input() orderInformation: AppComponentInputs['orderInformation'];
	@Input() shouldAnonymizeRx: AppComponentInputs['shouldAnonymizeRx'];
	@Input() enableAllCaseTypesForAddRx: AppComponentInputs['enableAllCaseTypesForAddRx'];
	@Input() prepareRx: AppComponentInputs['prepareRx'];
	@Input() authInfo: AppComponentInputs['authInfo'];
	@Input() getUnitType: AppComponentInputs['getUnitType'];
	@Input() shouldCheckSleeveConfirmationCheckBox: AppComponentInputs['shouldCheckSleeveConfirmationCheckBox'];
	@Input() forceDisplayTeethNumberingSystem: AppComponentInputs['forceDisplayTeethNumberingSystem'];
	@Input() printStylesName: AppComponentInputs['printStylesName'];
	@Input() region: AppComponentInputs['region'];
	@Input() viewMode: AppComponentInputs['viewMode'];
	@Output() sleeveConfirmationUpdated: EventEmitter<any> = new EventEmitter<any>();
	@Output() rxSaved: EventEmitter<RxSavedPayload> = this.facade.rxSaved;
	@Output() rxSaveFailed: EventEmitter<any> = this.facade.rxSaveFailed;
	@Output() rxValidated: EventEmitter<any> = new EventEmitter<any>();
	@Output() rxsPrinted: EventEmitter<any> = new EventEmitter<any>();
	@Output() rxsPrintedFailed: EventEmitter<any> = new EventEmitter<any>();
	@Output() pdfRequested: EventEmitter<any> = this.facade.pdfRequested;
	@Output() noteAdded: EventEmitter<any> = new EventEmitter<any>();
	@Output() getLabNotes: EventEmitter<any> = new EventEmitter<any>();
	@Output() orderInformationUpdated: EventEmitter<RxModelApi> = new EventEmitter<RxModelApi>();
	@Output() rxUpdated: EventEmitter<RxModelApi> = new EventEmitter<RxModelApi>();
	@Output() rxReady: EventEmitter<void> = new EventEmitter<void>();
	@Output() rxPrepared: EventEmitter<RxModelApi> = new EventEmitter<RxModelApi>();
	@Output() getUnitTypeConfiguration: EventEmitter<TeethUnitType[]> = new EventEmitter<TeethUnitType[]>();
	@Output() rxSend: EventEmitter<string> = new EventEmitter<string>();

	@Input() autoDisplayPrintViewInTheDOM: boolean;
	/**
	 * Flag that indicates, that app.component has a parent,
	 * in that case it is reasonable to drop rootable service's subscriptions in parent component (mostly related to SL)
	 * */
	@Input() hasOuterDestroyable = false;

	printAlive$ = new Subject<void>();

	title = 'Rx App';
	currentApplicationVersion = rxAppVersion?.version;
	configuration = [];
	isScanner: boolean;
	isPrintPortrait = true;
	readonly RoleTypeEnum = RoleTypeEnum;

	private trySaveRx$: Subject<boolean> = new Subject<boolean>();

	isHostPlatformScanner$: Observable<boolean> = this.hostPlatformService.isScanner$;
	userRole$ = this.facade.userRole$;
	isIntuitiveFlow$: Observable<boolean | undefined> = this.facade.isIntuitiveFlow$;
	isFallbackBannerAvailable$: Observable<boolean> = this.facade.isFallbackBannerAvailable$;

	@HostListener('window:keydown', ['$event'])
	handleKeyDown(event: KeyboardEvent) {
		if (this.isReadOnly && event.key === 'Tab') {
			event.preventDefault();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Base url should be first for translation to work
		if (changes.staticFilesEndpoint) {
			const staticFilesEndpoint = changes.staticFilesEndpoint?.currentValue;

			this.facade.updateStaticFilesEndpoint({ staticFilesEndpoint });
		}
		if (changes.authInfo) {
			const authInfo = changes.authInfo.currentValue;

			this.facade.updateAuthInfo(authInfo);
		}
		if (changes.languageCode) {
			const languageCode = changes.languageCode?.currentValue;

			this.facade.updateLanguageCode(languageCode);
			this.translateService.reloadLang(languageCode);
			this.translateService.use(languageCode);
		}
		if (changes.apiEndpoint) {
			const apiEndpoint = changes.apiEndpoint?.currentValue;

			this.facade.updateApiEndpoint({ apiEndpoint });
		}
		if (changes.rxAppApiEndpoint) {
			const rxAppApiEndpoint = changes.rxAppApiEndpoint?.currentValue;

			this.facade.updateRxAppApiEndpoint({ rxAppApiEndpoint });
		}
		if (changes.patientAppApiEndpoint) {
			const patientAppApiEndpoint = changes.patientAppApiEndpoint?.currentValue;

			this.facade.updatePatientAppApiEndpoint({ patientAppApiEndpoint });
		}
		if (changes.contactId) {
			const contactId = +changes.contactId?.currentValue;

			this.facade.updateContactId({ contactId });
		}
		if (changes.companyId) {
			const companyId = +changes.companyId?.currentValue;

			this.facade.updateCompanyId({ companyId });
		}
		if (changes.scannerId) {
			const scannerId = changes.scannerId?.currentValue;

			this.facade.updateScannerId({ scannerId });
		}
		if (changes.clientVersion) {
			const clientVersion = changes.clientVersion?.currentValue;

			this.facade.updateClientVersion({ clientVersion });
		}
		if (changes.packageVersion) {
			const packageVersion = changes.packageVersion?.currentValue;

			this.facade.updatePackageVersion({ packageVersion });
		}
		if (changes.runMode) {
			// const runMode = changes.runMode?.currentValue;
			// this.shellStore.update({ runMode }); // TODO: implement
		}
		if (changes.applicationMode) {
			const applicationMode = changes.applicationMode?.currentValue;

			this.facade.updateApplicationMode({ applicationMode });
		}
		if (changes.isOnline) {
			const isOnline = changes.isOnline?.currentValue;

			this.facade.updateIsOnline({ isOnline });
		}
		if (changes.productType) {
			const productType = changes.productType?.currentValue;

			this.facade.updateProductType({ productType });
		}
		if (changes.doctorId) {
			// const doctorId = changes.doctorId?.currentValue;
			// this.shellStore.update({ doctorId }); // TODO: implement
			// TODO: DOCTOR ID should also come from MIDC (Scanner only for now). When it does, filter doctor by doctor ID in shell-query
		}
		if (changes.rxId) {
			const rxId = changes.rxId?.currentValue;

			this.facade.updateRxId({ rxId });
		}
		if (changes.orderId) {
			const orderId: string = changes.orderId?.currentValue || null;

			if (orderId) {
				this.logger.info(`rx-app opened with orderId = ${orderId}`, { module: this.componentName });
			}

			this.facade.updateOrderId({ orderId });
		}
		if (changes.isReadOnly) {
			const isReadOnly = changes.isReadOnly?.currentValue;

			this.facade.updateIsReadOnly({ isReadOnly });
		}
		if (changes.readOnlyRules) {
			const readOnlyRules = changes.readOnlyRules?.currentValue;

			this.readOnlyRulesService.setRules({ readOnlyRules });
		}
		if (changes.patientGuid) {
			const patientGuid = changes.patientGuid?.currentValue || null;

			if (patientGuid && patientGuid !== emptyGuid) {
				this.shellStore.update({ patientGuidProvided: true });
				this.facade.loadPatient(patientGuid).pipe(take(1), takeUntil(this.componentAlive$)).subscribe();
			}
		}
		if (changes.saveRx) {
			this.trySaveRx$.next(false);
		}
		if (changes.saveRxScanner) {
			this.trySaveRx$.next(true);
		}
		if (changes.validateBeforeSend) {
			this.facade
				.validateBeforeSend()
				.pipe(
					tap(result => {
						if (!result.isValid) {
							this.logger.info('Attempt to send invalid RX', {
								module: this.componentName,
								extendedParameters: {
									traces: this.tracesQuery.configurationTraceIdsAsString + this.tracesQuery.savingGettingRxTraceIds,
									validationMessage: result.message
								}
							});
						}
					}),
					takeUntil(this.componentAlive$)
				)
				.subscribe(result => this.rxValidated.emit(result));
		}
		if (changes.isRxTakenForScan) {
			const isRxTakenForScan = changes.isRxTakenForScan?.currentValue;

			this.facade.updateIsRxTakenForScan({ isRxTakenForScan });
		}
		if (changes.authToken) {
			const authToken = changes.authToken?.currentValue;

			this.facade.updateAuthToken(authToken);
		}
		if (changes.validationMode) {
			const validationMode = changes.validationMode?.currentValue;

			this.facade.updateValidationMode(validationMode);
		}
		if (changes.userRole) {
			const userRole = changes.userRole?.currentValue;

			this.facade.updateUserRole(userRole);
		}
		if (changes.clonedFromRxId) {
			const clonedFromRxId = changes.clonedFromRxId?.currentValue;

			this.facade.updateClonedFromRxId(clonedFromRxId);
		}
		if (changes.isHeadlessPrint) {
			this.facade.updateIsHeadlessPrint({ isHeadlessPrint: changes.isHeadlessPrint?.currentValue });
		}
		if (changes.triggerPrintRx) {
			const triggerRXPrint = changes.triggerPrintRx?.currentValue;

			this.printAlive$.next();
			if (triggerRXPrint) {
				// TODO: use random value instead of boolean
				this.triggerPrintRx = false;

				const rxIdsForPrint = changes.rxIdsForPrint?.currentValue || this.rxIdsForPrint;
				const printOrientation = this.orientation === 'landscape' ? PrintOrientation.landscape : PrintOrientation.portrait;

				this.facade.updatePrintOrientation({ printOrientation });
				if (rxIdsForPrint?.length > 0) {
					this.printRx({ rxIds: rxIdsForPrint });
				}
			}
		}
		if (changes.forceV0) {
			const forceV0 = changes.forceV0?.currentValue;

			this.facade.updateForceV0(forceV0);
		}
		if (changes.isReturn) {
			const isReturn = changes.isReturn?.currentValue;

			this.facade.updateIsReturn(isReturn);
		}
		if (changes.isResendScan) {
			const isResendScan = changes.isResendScan?.currentValue ?? false;

			this.facade.updateIsResendScan(isResendScan);
		}
		if (changes.addNote) {
			const noteData = changes.addNote?.currentValue;
			const addedNote = this.notesService.addLabNote(noteData);

			this.readOnlyRulesService.update({ notes: false });

			this.noteAdded.emit(addedNote);
		}
		if (changes.orderInformation) {
			if (this.shellQuery.userRole !== RoleTypeEnum.Lab) {
				return;
			}

			const orderInformation = changes.orderInformation?.currentValue;

			this.facade.updateOrderInformation(orderInformation);
			this.orderInformationUpdated.next(this.saveRxService.prepareRxModelToSave(this.shellQuery.rx));
		}
		if (changes.shouldAnonymizeRx) {
			this.facade.updateShouldAnonymizeRx(changes.shouldAnonymizeRx.currentValue);
		}
		if (changes.autoDisplayPrintViewInTheDOM) {
			this.shellStore.update({ autoDisplayPrintViewInTheDOM: changes.autoDisplayPrintViewInTheDOM.currentValue });
		}
		if (changes.enableAllCaseTypesForAddRx) {
			const enableAllCaseTypesForAddRx = changes.enableAllCaseTypesForAddRx.currentValue;

			this.facade.updateEnableAllCaseTypesForAddRx(enableAllCaseTypesForAddRx);
		}
		if (changes.changeUnitType) {
			const changeUnitType = changes.changeUnitType.currentValue;

			this.facade.changeUnitType(changeUnitType);
		}
		if (changes.prepareRx) {
			this.rxPrepared.emit(this.saveRxService.prepareRxModelToSave(this.shellQuery.rx));
		}
		if (changes.shouldCheckSleeveConfirmationCheckBox) {
			const isSleeveChecked = changes.shouldCheckSleeveConfirmationCheckBox.currentValue;

			this.facade.updateIsSleeveCheckedSentByScanner(isSleeveChecked);
		}
		if (changes.forceDisplayTeethNumberingSystem) {
			const forceDisplayTeethNumberingSystem = changes.forceDisplayTeethNumberingSystem.currentValue;

			this.facade.updateForceDisplayTeethNumberingSystem(forceDisplayTeethNumberingSystem);
		}

		if (changes.getUnitType) {
			const config = this.rxRulesService.getUnitTypeConfiguration(this.shellQuery.rx);

			this.getUnitTypeConfiguration.emit(config ?? undefined);
		}

		if (changes.printStylesName) {
			const printStyleName = changes.printStylesName?.currentValue;

			this.facade.updatePrintStyleName(printStyleName);
		}
		if (changes.region) {
			const region = changes.region.currentValue;

			this.facade.updateRegion(region);
		}

		if (changes.viewMode) {
			const viewMode: ViewModeEnum = changes.viewMode.currentValue;

			if (viewMode) {
				this.facade.updateViewMode(viewMode);
			}
		}
	}

	ngOnInit(): void {
		this.subscribeToWindowResize();

		this.isIntuitiveFlow$
			.pipe(
				distinctUntilChanged(),
				tap(isIntuitiveFlow => {
					if (isIntuitiveFlow === false) {
						this.renderer.addClass(this.documentRef.documentElement, this.legacyToolkitFontsBodyClass);

						return;
					}

					this.renderer.removeClass(this.documentRef.documentElement, this.legacyToolkitFontsBodyClass);
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.initSplunkRum();

		this.facade.updateRxSessionId(uuid());
		void this.fontFaceService.load();
		this.facade.pdfRequested$.pipe(takeUntil(this.componentAlive$)).subscribe();

		this.facade.resetPreparedNewRxId();
		this.facade.loadConfigurationAndRxIfNeeded().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.facade.loadLinkedCompanies().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.customMaterialsService.loadCustomMaterials().pipe(takeUntil(this.componentAlive$)).subscribe();

		this.facade.loadAssetsForCache();
		if (this.shellQuery.userRole === RoleTypeEnum.Lab) {
			this.setLabNotesListener();
		}

		this.setRxReadyListener();
		if (this.hostPlatformService.isIteroModeling || this.shellQuery.userRole === RoleTypeEnum.Lab) {
			this.setRxLoadedListener();
		}

		this.rxFormValidationListener();
		this.matIconService.registerMatIcons();
		this.notesService.setNotesArray().subscribe();
		this.aligntechNotesService.setAlignTechNotesArray().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.translateSelectOptions().subscribe();
		this.logRxAppVersion();
		this.subscribeToTrackSleeveConfirmationUpdate();
		this.customMaterialsService.enrichMaterialRules().pipe(takeUntil(this.componentAlive$)).subscribe();

		this.trySaveRx$
			.pipe(
				exhaustMap(isCalledByScanner => this.facade.tryToSaveRx(isCalledByScanner)),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		// Initialize remote application loader for embedded remote applications
		this.shellQuery.appSettings$
			.pipe(
				filter(appSettings => !!appSettings?.remoteApplicationLoaderUrl && appSettings.remoteApplicationLoaderUrl.length > 0),
				take(1),
				switchMap(appSettings =>
					from(
						initializeRemoteApplicationLoader(this.environmentInjector, {
							ralUrl: appSettings.remoteApplicationLoaderUrl,
							initOptions: {
								ngZone: this.ngZone
							}
						})
					).pipe(
						catchError(error => {
							this.logger.error('Failed to initialize remote application loader', {
								module: 'AppComponent',
								extendedParameters: { error, ralUrl: appSettings.remoteApplicationLoaderUrl }
							});

							return of(null);
						})
					)
				),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.patientAppIframeCommunicationService.patientAppPopupClose();
		this.unsubscribeFromPrint();
		this.unifiedPatientsIntegrationService.reset();
		SplunkRum.deinit();
		super.ngOnDestroy();
		resetStores({ exclude: [localSettingsStoreName] });

		this.renderer.removeClass(this.documentRef.documentElement, this.legacyToolkitFontsBodyClass);

		if (!this.hasOuterDestroyable) {
			this.facade.destroyBiSession();
			this.logger.ngOnDestroy();
		}
	}

	ngAfterViewInit(): void {
		this.facade.initPendo().pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	subscribeToTrackSleeveConfirmationUpdate() {
		this.facade.sleeveCheckedChangedByUser$
			.pipe(
				tap((isSleeveChecked: boolean) => this.sleeveConfirmationUpdated.next(isSleeveChecked)),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	printRx({ rxIds }: { rxIds: number[] }): void {
		try {
			this.facade
				.getRxsBulk({ rxIds })
				.pipe(
					switchMap(() => this.printService.createPrintIframeElement()),
					switchMap(printRxIframe => {
						const compFactory = this.cfr.resolveComponentFactory(PrintRxComponent);
						const injector = Injector.create({
							providers: [{ provide: PRINT_IFRAME_ELEMENT, useValue: printRxIframe }],
							parent: this.injector
						});

						this.printVcr.clear();

						const compRef = this.printVcr.createComponent(compFactory, 0, injector);

						compRef.changeDetectorRef.detectChanges();

						this.renderer.appendChild(printRxIframe.contentDocument?.body, compRef.location.nativeElement);

						return fromEvent(printRxIframe.contentWindow as any, 'afterprint');
					}),
					delay(0), // need to emit event asynchroniously because context was on print preview
					tap(() => this.rxsPrinted.emit()),
					catchError(err => {
						this.rxsPrintedFailed.emit(err);

						return of(err);
					}),
					takeUntil(merge(this.componentAlive$, this.printAlive$))
				)
				.subscribe();

			if (!this.shellQuery.apiEndpointFallback) {
				this.toggleOrientation();
			}
		} catch (err) {
			this.rxsPrintedFailed.next(err);
		}
	}

	mockGetRx(): void {
		this.facade.mockGetRx().pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	toggleOrientation() {
		this.orientation = this.isPrintPortrait ? 'portrait' : 'landscape';
		this.facade.updatePrintOrientation({
			printOrientation: this.orientation === 'landscape' ? PrintOrientation.landscape : PrintOrientation.portrait
		});
	}

	rxFormValidationListener(): void {
		this.facade.validateRxForSave().subscribe();
	}

	callTryToSaveRx(): void {
		this.facade.tryToSaveRx(false).subscribe();
	}

	setLabNotesListener(): void {
		this.notesQuery.labNotes$
			.pipe(
				filter(notes => !!notes),
				tap(notes => this.getLabNotes.emit(notes)),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	setRxReadyListener(): void {
		combineLatest([this.shellQuery.rxConfiguration$, this.shellQuery.companies$])
			.pipe(
				filter(([rxCfg, companies]) => !!rxCfg && !!companies),
				take(1),
				tap(() => this.rxReady.emit()),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	setRxLoadedListener(): void {
		this.shellQuery.rx$
			.pipe(
				filter((rx): rx is RxModel => !!rx),
				tap(rx => this.rxUpdated.emit(this.saveRxService.prepareRxModelToSave(rx))),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	sendRx(): void {
		this.facade
			.validateBeforeSend()
			.pipe(
				tap(result => {
					const validationResult = this.rxSummaryService.validateRxForSummaryPage(result.isValid);

					if (validationResult.valid && validationResult.rxId) {
						this.rxSend.emit(validationResult.rxId);
					}
				}),
				take(1),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private logRxAppVersion() {
		if (rxAppVersion?.version) {
			this.logger.info(`Rx-App loaded - version: ${rxAppVersion?.version}`, {
				module: this.componentName,
				extendedParameters: { rxAppVersion: rxAppVersion?.version }
			});
		} else {
			this.logger.error('Rx-App version number Failed to load.', {
				module: this.componentName
			});
		}
	}

	private translateSelectOptions(): Observable<TranslationChangeEvent> {
		return this.translateService.onLangChange.pipe(
			takeUntil(this.componentAlive$),
			tap(() => this.rxRulesService.translateSelectOptions())
		);
	}

	private saveRxForLab(): Observable<any> {
		return this.saveRxService.saveRx().pipe(this.facade.afterSaveRx());
	}

	private unsubscribeFromPrint() {
		this.printAlive$.next();
		this.printAlive$.complete();
	}

	private subscribeToWindowResize(): void {
		fromEvent(this.windowRef, 'resize')
			.pipe(
				debounceTime(debounceDelay),
				map(event => (event.currentTarget as Window)?.innerHeight),
				startWith(this.windowRef.innerHeight),
				distinctUntilChanged(),
				tap(() => {
					const activeElement = this.documentRef.activeElement;

					if (activeElement && (this.vcRef.element.nativeElement as HTMLElement).contains(activeElement)) {
						activeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private initSplunkRum() {
		const signalFx = this.appSettingsService.splunkSettings;

		if (signalFx?.isEnabled && !SplunkRum.inited) {
			try {
				SplunkRum.init({
					realm: signalFx.realm,
					rumAccessToken: signalFx.rumAuth,
					applicationName: signalFx.app,
					deploymentEnvironment: signalFx.environment,
					version: rxAppVersion?.version,
					ignoreUrls: [/\/assets\/.*\.svg$/i],
					globalAttributes: {
						microfrontend: 'rx-app',
						company: this.companyId,
						contact: this.contactId,
						applicationMode: this.applicationMode,
						orderId: this.orderId,
						rxId: this.rxId
					}
				});
			} catch (e) {
				this.logger.error('SplunkRum.init failed', {
					module: this.componentName,
					extendedParameters: { error: JSON.stringify(e) }
				});
			}
		}
	}
}
